import { useEffect } from 'react';

import { OverdueBalanceInitializer } from '@ctct/application-layer';
import { BillingStrategy, createResolvedBillingStrategy } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import { Card } from '@ctct/payflow-ui';
import { PayflowTopBar } from '@ctct/presentation-layer';
import {
  isStripeParamEnabled,
  isStripePhase2ParamEnabled,
  AlertWidget,
  BillingInfoFormWidget,
  OverduePaymentButtonWidget,
  OverdueSummaryWidget,
  PaymentMethodsWidget,
  useAlertWidget,
  isApplePayParamEnabled,
  useOverduePaymentButtonWidget,
  useOverdueSummaryWidget,
  usePaymentMethodsWidget,
  useRouterNonUiWidget,
  useUpdateBillingWidget,
  useBillingInfoFormWidget,
} from '@ctct/react-app-layer';
import {
  createAlertHooks,
  createBillingInfoFormHooks,
  createOverduePaymentButtonHooks,
  createPaymentMethodsHooks,
  createUpdateBillingHooks,
  useOverdueBalancePageStore,
} from '@ctct/state-client-layer';
import { useBillingAccount, usePaymentStrategy } from '@ctct/state-server-layer';

import { RedirectToStripeOverdueBalancePage } from './RedirectToStripeOverdueBalancePage';

import './overdue-balance-page.scss';

const linkToDashboard = `https://app${envs.getEnv()}constantcontact.com/home`;

const initializer = new OverdueBalanceInitializer();
const overduePaymentButtonHooks = createOverduePaymentButtonHooks(useOverdueBalancePageStore);
const updateBillingHooks = createUpdateBillingHooks(useOverdueBalancePageStore);
const paymentMethodsHooks = createPaymentMethodsHooks(useOverdueBalancePageStore);
const alertHooks = createAlertHooks(useOverdueBalancePageStore);
const billingInfoFormHooks = createBillingInfoFormHooks(useOverdueBalancePageStore);

const paymentMethodsExternalHooks = {
  useRatingCallPayload: () => null,
};

const routerNonUiExternalHooks = {
  useIsHidePromoCodesEnabled: () => false, // no promo code application on this page
};

const isStripeEnabled = isStripeParamEnabled() ?? false;
const isStripePhase2Enabled = isStripePhase2ParamEnabled() ?? false;
const billingStrategy = createResolvedBillingStrategy(
  isStripeEnabled ? BillingStrategy.Stripe : BillingStrategy.InHouse
);

export const OverdueBalancePage = () => {
  const isApplePayEnabled = isApplePayParamEnabled();
  const { data: billingAccount } = useBillingAccount();
  const paymentStrategy = usePaymentStrategy({
    billingStrategyQuery: billingStrategy,
  });

  const overdueSummaryWidgetProps = useOverdueSummaryWidget({
    initializer,
  });

  const overduePaymentButtonWidgetProps = useOverduePaymentButtonWidget({
    initializer,
    pageStore: useOverdueBalancePageStore,
    hooks: overduePaymentButtonHooks,
  });
  const paymentMethodsWidgetProps = usePaymentMethodsWidget({
    initializer,
    pageStore: useOverdueBalancePageStore,
    hooks: paymentMethodsHooks,
    isApplePayEnabled,
    billingStrategy,
    paymentStrategy,
    externalHooks: paymentMethodsExternalHooks,
    flags: {
      shouldIgnoreRatingCall: true,
    },
  });
  const alertWidgetProps = useAlertWidget({
    initializer,
    pageStore: useOverdueBalancePageStore,
    hooks: alertHooks,
  });

  const billingInfoFormWidgetProps = useBillingInfoFormWidget({
    initializer,
    pageStore: useOverdueBalancePageStore,
    hooks: billingInfoFormHooks,
    billingStrategy,
  });

  useRouterNonUiWidget({
    initializer,
    isStripeEnabled,
    isStripePhase2Enabled,
    externalHooks: routerNonUiExternalHooks,
  });
  useUpdateBillingWidget({
    initializer,
    pageStore: useOverdueBalancePageStore,
    hooks: updateBillingHooks,
  });

  useEffect(() => {
    document.body.setAttribute('data-tracking-pagename', 'overdue-balance');
  }, []);

  useEffect(() => {
    initializer.listeners.on();

    return () => {
      initializer.listeners.off();
    };
  }, []);

  const isStripeAccount = billingAccount?.getIsStripeAccount() ?? false;

  if (isStripeAccount) {
    return <RedirectToStripeOverdueBalancePage />;
  }

  return (
    <>
      <PayflowTopBar exitLink={linkToDashboard} logoLink={linkToDashboard} />
      <div className="overdue-balance-page-container">
        <AlertWidget {...alertWidgetProps} />
        <div className="overdue-balance-page-content">
          <Card className="overdue-balance-page_payment-methods">
            <PaymentMethodsWidget {...paymentMethodsWidgetProps} />
            <BillingInfoFormWidget {...billingInfoFormWidgetProps} />
          </Card>
          <Card className="overdue-balance-page_summary">
            <OverdueSummaryWidget {...overdueSummaryWidgetProps} />
            <OverduePaymentButtonWidget {...overduePaymentButtonWidgetProps} />
          </Card>
        </div>
      </div>
    </>
  );
};
